/**
 * Чат-экран (W2/W4): история из канона `messages`, отправка через
 * `POST /chat/send`, живые обновления через stream (SSE/polling).
 *
 * Контейнер — content-зона shell'а (Dual-Core IA §7): header/язык/logout
 * живут в `main.ts`. text_delta из LLM недоступен (спека §6.2) →
 * typing-индикатор: «Ника думает…» с момента отправки и до ответа.
 * Оптимистичный пузырь — на клиенте (§6.2); дублей нет благодаря серверным
 * id строк (user_msg_id/reply_msg_id): каждое сообщение рисуется ровно один
 * раз, поздние stream-строки с уже виденным id отбрасываются.
 *
 * Операционные карточки (W-мутации, §8, DoD-4): ход может выдать черновик
 * confirm-gate — карточка с «Подтвердить/Отклонить» рисуется из ответа
 * chat-send, из SSE `operation_proposed` и восстанавливается после
 * перезагрузки через `GET /operations/active` (D2 персистентность).
 * Подтверждение — сверка payload_hash (I-5); слот один: новый черновик
 * гасит старую карточку (экспирация на сервере — D2).
 *
 * PD-гейт «настоящий»: 403 `pd_consent_required` с сервера → колбэк
 * `onPdRequired` (shell показывает экран согласия, чат не работает без него).
 *
 * Вложения (M1, §9.2): 📎 — фото/видео (D3 presign→PUT→complete, чипы до
 * отправки, consume в chat/send); 🎤 — голос через MediaRecorder →
 * voice_note → STT на complete → транскрипт уходит Нике СРАЗУ, без шага
 * «вставить в поле ввода → Отправить» (owner-decision 261001-vgd: лишний
 * мув мешал общению). Полный транскрипт виден пузырём — ошибку распознавания
 * можно сразу поправить следующим сообщением; черновик в поле ввода не
 * трогаем. §6.2-контракт сохранён: voice_note в chat/send запрещён —
 * уходит именно текстом.
 */

import {
  type ChatMessage,
  type OperationCard,
  ApiError,
  confirmOperation,
  fetchTodayCached,
  getActiveOperation,
  getMessages,
  rejectOperation,
  sendMessage,
  uploadFile,
} from "./api";
import { getLang, plural, t } from "./i18n";
import { icon } from "./icons";
import { startStream, type StreamStatus } from "./stream";

export interface ChatHandlers {
  onPdRequired?: () => void;
  /** v2.1 UX-05: suggest-чипсы участника (утро/вечер); тренеру — нет. */
  suggestChips?: boolean;
  /** v2.1 UX-09: чипс «Итоги тренировки» для тренера. */
  suggestCoach?: boolean;
}

export interface ChatView {
  activate(): void;
  deactivate(): void;
  dispose(): void;
  /** v2.1 UX-13: prefill от «Редактировать с Никой» (без авто-отправки). */
  prefill(text: string): void;
}

/** 403 `pd_consent_required` (тело может быть обёрнуто в `detail`). */
function isPdRequired(err: unknown): boolean {
  if (!(err instanceof ApiError) || err.status !== 403) return false;
  const body = err.detail as Record<string, unknown> | null;
  const inner =
    body && typeof body === "object" && "detail" in body
      ? (body.detail as Record<string, unknown>)
      : body;
  return Boolean(
    inner && typeof inner === "object" && inner.error === "pd_consent_required",
  );
}

// kind черновика → i18n-лейбл (`ops.kind.*`); неизвестный kind — ops.kind.other.
const KNOWN_KINDS = new Set([
  "schedule_upsert",
  "coach_message",
  "coach_broadcast",
  "coach_relay",
  "program_save",
  "program_phase",
  "own_program",
]);

const kindLabel = (kind: string): string =>
  KNOWN_KINDS.has(kind) ? t(`ops.kind.${kind}`) : t("ops.kind.other");

const formatTime = (iso: string): string => {
  try {
    return new Intl.DateTimeFormat(getLang(), {
      hour: "2-digit",
      minute: "2-digit",
    }).format(new Date(iso));
  } catch {
    return iso;
  }
};

export function renderChat(root: HTMLElement, handlers: ChatHandlers = {}): ChatView {
  let disposed = false;
  let active = true;
  let savedScroll = 0;
  let followBottom = true;
  let stopStream: (() => void) | null = null;
  root.innerHTML = `
    <div class="chat">
      <div class="chat__statusbar" role="status" aria-live="polite">
        <span class="chat__status" id="chat-status">${t("chat.status.off")}</span>
      </div>

      <div class="chat__log" id="chat-log" role="log" aria-live="polite" aria-relevant="additions">
        <p class="chat__empty" id="chat-empty">${t("chat.empty")}</p>
      </div>

      <div class="chat__typing" id="chat-typing" hidden>${t("chat.typing")}</div>

      <div class="chat__chips" id="chat-chips" hidden></div>
      <div class="chat__suggest" id="chat-suggest" hidden></div>
      <footer class="chat__composer">
        <input type="file" id="chat-files" accept="image/*,video/*" multiple hidden>
        <button class="chat__tool" id="chat-attach" aria-label="${t("chat.attach")}"
                title="${t("chat.attach")}">${icon("attach")}</button>
        <button class="chat__tool" id="chat-voice" aria-label="${t("chat.voice")}"
                title="${t("chat.voice")}" hidden>${icon("voice")}</button>
        <textarea class="chat__input" id="chat-input" rows="1"
                  aria-label="${t("chat.placeholder")}" placeholder="${t("chat.placeholder")}"></textarea>
        <button class="chat__send" id="chat-send" aria-label="${t("chat.send")}" title="${t("chat.send")}">${icon("send")}</button>
      </footer>
    </div>
  `;

  const log = root.querySelector<HTMLElement>("#chat-log")!;
  const empty = root.querySelector<HTMLElement>("#chat-empty")!;
  const input = root.querySelector<HTMLTextAreaElement>("#chat-input")!;
  const sendBtn = root.querySelector<HTMLButtonElement>("#chat-send")!;
  const typing = root.querySelector<HTMLElement>("#chat-typing")!;
  const statusEl = root.querySelector<HTMLElement>("#chat-status")!;
  const chipsEl = root.querySelector<HTMLElement>("#chat-chips")!;
  const filesInput = root.querySelector<HTMLInputElement>("#chat-files")!;
  const attachBtn = root.querySelector<HTMLButtonElement>("#chat-attach")!;
  const voiceBtn = root.querySelector<HTMLButtonElement>("#chat-voice")!;

  const seen = new Set<number>();                       // реальные id из канона
  const bubblesById = new Map<number, HTMLElement>();
  const pendingByClientId = new Map<string, HTMLElement>(); // оптимистичные пузыри
  let pseudoId = -1;
  let sending = false;
  let currentOp: { id: string; el: HTMLElement } | null = null;

  // ── вложения (M1, §9.2): загрузка meal-фото/видео до отправки ──────────────
  const chips: Array<{ id: string | null; el: HTMLElement }> = [];

  const removeChip = (el: HTMLElement): void => {
    const i = chips.findIndex((c) => c.el === el);
    if (i >= 0) chips.splice(i, 1);
    el.remove();
    chipsEl.hidden = chips.length === 0;
  };

  const addChip = (label: string): HTMLElement => {
    const el = document.createElement("div");
    el.className = "chip";
    const name = document.createElement("span");
    name.className = "chip__name";
    name.textContent = label;
    const x = document.createElement("button");
    x.className = "chip__x";
    x.setAttribute("aria-label", "✕");
    x.textContent = "✕";
    x.addEventListener("click", () => removeChip(el));
    el.append(name, x);
    chipsEl.appendChild(el);
    chipsEl.hidden = false;
    return el;
  };

  const clearChips = (): void => {
    chips.length = 0;
    chipsEl.innerHTML = "";
    chipsEl.hidden = true;
  };

  const systemRow = (content: string): void => {
    renderRow({
      id: pseudoId--,
      role: "system",
      content,
      created_at: new Date().toISOString(),
      origin_channel: "pwa",
    });
  };

  const scrollBottom = () => {
    if (active && followBottom) log.scrollTop = log.scrollHeight;
  };
  log.addEventListener("scroll", () => {
    if (active) followBottom = log.scrollHeight - log.scrollTop - log.clientHeight < 64;
  });

  const appendTime = (el: HTMLElement, iso: string): void => {
    if (!iso || !Number.isFinite(new Date(iso).getTime())) return;
    const time = document.createElement("time");
    time.className = "bubble__time";
    time.dateTime = iso;
    time.textContent = formatTime(iso);
    el.appendChild(time);
  };

  const setTyping = (on: boolean) => {
    typing.hidden = !on;
    if (on) scrollBottom();
  };

  const setSending = (on: boolean) => {
    sending = on;
    sendBtn.disabled = on;
    setTyping(on);
  };

  /** Единственная точка рендера строки канона: дубли невозможны (по id). */
  const renderRow = (row: ChatMessage): HTMLElement | null => {
    if (disposed) return null;
    // Оптимистичный пузырь уже показывает это сообщение → сверяем каноном.
    const pending = row.client_msg_id
      ? pendingByClientId.get(row.client_msg_id)
      : undefined;
    if (pending && row.id > 0) {
      seen.add(row.id);
      bubblesById.set(row.id, pending);
      pending.classList.remove("bubble--pending");
      if (!pending.querySelector("time")) appendTime(pending, row.created_at);
      pendingByClientId.delete(row.client_msg_id!);
      return null;
    }
    if (row.id > 0 && seen.has(row.id)) {
      const existing = bubblesById.get(row.id);
      if (existing && !existing.querySelector("time")) appendTime(existing, row.created_at);
      return null;
    }
    if (row.id > 0) seen.add(row.id);
    empty.hidden = true;
    const div = document.createElement("div");
    div.className =
      "bubble " + (row.role === "user" ? "bubble--me" : "bubble--coach");
    div.textContent = row.content;
    if (row.role === "assistant" && row.help_topics?.length) {
      const topics = document.createElement("div");
      topics.className = "chat__help-topics";
      for (const topic of row.help_topics) {
        const button = document.createElement("button");
        button.type = "button";
        button.className = "suggest-chip";
        button.textContent = topic.label;
        button.addEventListener("click", () => void doSend(`/help ${topic.key}`, true));
        topics.appendChild(button);
      }
      div.appendChild(topics);
    }
    // Время из канона; локальные служебные строки не выдают клиентское время за серверное.
    if (row.id > 0) appendTime(div, row.created_at);
    log.appendChild(div);
    if (row.id > 0) bubblesById.set(row.id, div);
    scrollBottom();
    return div;
  };

  const addLocalEcho = (content: string, clientMsgId: string): void => {
    empty.hidden = true;
    const div = document.createElement("div");
    div.className = "bubble bubble--me bubble--pending";
    div.textContent = content;
    log.appendChild(div);
    pendingByClientId.set(clientMsgId, div);
    scrollBottom();
  };

  // ── операционные карточки (Confirm-Gate, §8) ───────────────────────────────

  const draftRow = (key: string, value: unknown): HTMLElement => {
    const row = document.createElement("div");
    row.className = "opcard__row";
    const k = document.createElement("span");
    k.className = "opcard__key";
    k.textContent = key;
    const v = document.createElement("span");
    v.className = "opcard__val";
    const text =
      typeof value === "object" && value !== null
        ? JSON.stringify(value)
        : String(value);
    v.textContent = text.length > 120 ? text.slice(0, 117) + "…" : text;
    row.append(k, v);
    return row;
  };

  /** Статус вместо кнопок (keep=false) или под ними (retry-случай сети). */
  const setStatus = (
    el: HTMLElement,
    text: string,
    mod: string,
    keepButtons = false,
  ): void => {
    // display, а не hidden: .opcard__actions стилизован display:flex.
    el.querySelector<HTMLElement>(".opcard__actions")!.style.display =
      keepButtons ? "flex" : "none";
    const st = el.querySelector<HTMLElement>(".opcard__status")!;
    st.hidden = false;
    st.textContent = text;
    st.className = `opcard__status opcard__status--${mod}`;
  };

  const closeCard = (el: HTMLElement, text: string, mod: string): void => {
    setStatus(el, text, mod, false);
    if (currentOp?.el === el) currentOp = null;
  };

  const renderOpCard = (card: OperationCard): void => {
    if (disposed) return;
    if (currentOp && currentOp.id === card.operation_id) return;
    // Слот один (D2): новый черновик заменяет старый — старую карточку гасим.
    if (currentOp) {
      closeCard(currentOp.el, t("ops.expired"), "expired");
    }
    empty.hidden = true;
    const el = document.createElement("div");
    el.className = "opcard";
    el.dataset.op = card.operation_id;

    const title = document.createElement("div");
    title.className = "opcard__title";
    title.textContent = t("ops.title");

    const kind = document.createElement("div");
    kind.className = "opcard__kind";
    kind.textContent = kindLabel(card.kind);

    const fields = document.createElement("div");
    fields.className = "opcard__fields";
    for (const [key, value] of Object.entries(card.draft ?? {})) {
      fields.appendChild(draftRow(key, value));
    }

    const exp = document.createElement("div");
    exp.className = "opcard__expiry";
    exp.textContent = `${t("ops.expires")} ${formatTime(card.expires_at)}`;

    const actions = document.createElement("div");
    actions.className = "opcard__actions";
    const confirmBtn = document.createElement("button");
    confirmBtn.className = "opcard__confirm";
    confirmBtn.textContent = t("ops.confirm");
    const rejectBtn = document.createElement("button");
    rejectBtn.className = "opcard__reject";
    rejectBtn.textContent = t("ops.reject");
    actions.append(confirmBtn, rejectBtn);

    const status = document.createElement("div");
    status.className = "opcard__status";
    status.hidden = true;

    el.append(title, kind, fields, exp, actions, status);
    log.appendChild(el);
    currentOp = { id: card.operation_id, el };
    scrollBottom();

    const setBusy = (on: boolean) => {
      confirmBtn.disabled = on;
      rejectBtn.disabled = on;
    };

    confirmBtn.addEventListener("click", () =>
      void (async () => {
        setBusy(true);
        try {
          const res = await confirmOperation(
            card.operation_id,
            card.payload_hash,
          );
          const receiptOk = Boolean(
            (res.receipt as { ok?: boolean } | undefined)?.ok,
          );
          // Даже сбой тула — черновик израсходован (pop-first): карточка
          // закрывается честным вердиктом, повторить нельзя.
          closeCard(
            el,
            receiptOk ? t("ops.confirmed") : t("ops.failed"),
            receiptOk ? "confirmed" : "failed",
          );
        } catch (err) {
          const code = err instanceof ApiError ? err.status : 0;
          if (code === 404 || code === 409) {
            // I-5/слот: черновик сменён или израсходован — карточка устарела.
            closeCard(
              el,
              code === 409 ? t("ops.mismatch") : t("ops.expired"),
              "expired",
            );
          } else {
            // Сеть: операция могла исполниться — повтор даст 404 (устарело).
            setStatus(el, t("ops.failed"), "failed", true);
            setBusy(false);
          }
        }
      })(),
    );

    rejectBtn.addEventListener("click", () =>
      void (async () => {
        setBusy(true);
        try {
          await rejectOperation(card.operation_id);
        } catch {
          /* reject идемпотентен по смыслу: карточка гаснет в любом случае */
        }
        closeCard(el, t("ops.rejected"), "rejected");
      })(),
    );
  };

  // Очередь отправки (261001-vgd): голос авто-отправкой и ручная отправка
  // сериализуются — голос, сказанный во время ответа Ники, не теряется.
  let sendChain: Promise<void> = Promise.resolve();
  const enqueueSend = (run: () => Promise<void>): void => {
    sendChain = sendChain.then(run, run);
    void sendChain;
  };

  /** overrideText — голос: содержимое не читаем из поля, черновик не трогаем. */
  const doSend = async (overrideText?: string, helpCommand = false) => {
    const content = (overrideText ?? input.value).trim();
    const mediaIds = (helpCommand ? [] : chips)
      .map((c) => c.id)
      .filter((v): v is string => Boolean(v));
    if (disposed || (!content && mediaIds.length === 0) || sending) return;
    // Вложения ещё грузятся: ручную отправку ждём (chip потеряется молча);
    // голос уходит с готовыми чипами, догружающийся остаётся на следующий ход.
    if (overrideText === undefined && chips.some((c) => c.id === null)) return;
    // content обязателен (§6.2) — медиа без текста шлём с нейтральной подписью.
    const text = content || t("chat.media_default");
    const clientMsgId =
      typeof crypto !== "undefined" && "randomUUID" in crypto
        ? crypto.randomUUID()
        : `c-${Date.now()}-${Math.random().toString(36).slice(2)}`;
    if (overrideText === undefined) {
      input.value = "";
      input.style.height = "auto";
    }
    addLocalEcho(text, clientMsgId);
    followBottom = true;
    scrollBottom();
    setSending(true);
    try {
      const res = await sendMessage(text, clientMsgId, mediaIds);
      if (disposed) return;
      // Вложения израсходованы ходом (server-side consume) — чипы сняты.
      if (!helpCommand) clearChips();
      // Канонические id хода: помечаем оптимистичный пузырь сверенным и
      // рисуем ответ ровно один раз (stream-строки с этими id отбросятся).
      const pending = pendingByClientId.get(clientMsgId);
      if (res.user_msg_id && res.user_msg_id > 0) {
        seen.add(res.user_msg_id);
        if (pending) bubblesById.set(res.user_msg_id, pending);
        pending?.classList.remove("bubble--pending");
        pendingByClientId.delete(clientMsgId);
        // SSE не зеркалит собственные PWA user-строки. Дата эха — из read API.
        const userId = res.user_msg_id;
        void getMessages(Math.max(0, userId - 1), 2).then(({ messages }) => {
          if (disposed) return;
          const canonical = messages.find((row) => row.id === userId);
          if (canonical) renderRow(canonical);
        }).catch(() => undefined);
      }
      if (res.reply) {
        renderRow({
          id: res.reply_msg_id ?? pseudoId--,
          role: "assistant",
          content: res.reply,
          help_topics: res.help_topics,
          created_at: "",
          origin_channel: "pwa",
        });
      }
      // Ход выдал черновик confirm-gate → карточка сразу (§8).
      if (res.operation) renderOpCard(res.operation);
    } catch (err) {
      if (disposed) return;
      pendingByClientId.get(clientMsgId)?.classList.remove("bubble--pending");
      pendingByClientId.delete(clientMsgId);
      if (isPdRequired(err)) {
        handlers.onPdRequired?.();
        return;
      }
      renderRow({
        id: pseudoId--,
        role: "system",
        content: t("chat.error.send"),
        created_at: new Date().toISOString(),
        origin_channel: "pwa",
      });
    } finally {
      setSending(false);
    }
  };

  sendBtn.addEventListener("click", () => enqueueSend(() => doSend()));
  input.addEventListener("keydown", (ev) => {
    if (ev.key === "Enter" && !ev.shiftKey && !ev.isComposing) {
      ev.preventDefault();
      enqueueSend(() => doSend());
    }
  });
  input.addEventListener("input", () => {
    input.style.height = "auto";
    input.style.height = Math.min(input.scrollHeight, 120) + "px";
  });

  // ── 📎 вложение: presign → PUT → complete, chip ждёт отправки (§9.1) ──────
  // chipsLoading — хвост всех батчей загрузки: голос авто-отправкой ждёт его,
  // чтобы фото/видео попали в то же сообщение, что и транскрипт.
  let chipsLoading: Promise<void> = Promise.resolve();
  attachBtn.addEventListener("click", () => filesInput.click());
  filesInput.addEventListener("change", () => {
    const files = Array.from(filesInput.files ?? []);
    filesInput.value = "";
    const batch = async () => {
      for (const file of files) {
        if (disposed) return;
        if (chips.length >= 10) break; // кап как у TG-альбома
        const el = addChip(file.name || t("chat.attach"));
        const entry: { id: string | null; el: HTMLElement } = { id: null, el };
        chips.push(entry);
        try {
          const res = await uploadFile(file, "meal");
          entry.id = res.media.media_id;
          el.classList.add("chip--ready");
        } catch {
          removeChip(el);
          systemRow(t("chat.error.upload"));
        }
      }
    };
    chipsLoading = chipsLoading.then(batch, batch);
    void chipsLoading;
  });

  // ── 🎤 голос: MediaRecorder → voice_note (STT на complete) → текст (§6.2) ─
  const canVoice =
    typeof window.MediaRecorder !== "undefined" &&
    Boolean(navigator.mediaDevices?.getUserMedia);
  if (canVoice) voiceBtn.hidden = false;

  let recorder: MediaRecorder | null = null;
  let voiceGeneration = 0;
  let voiceStarting = false;
  const discardVoice = (): void => {
    voiceGeneration++;
    if (recorder && recorder.state !== "inactive") recorder.stop();
  };
  voiceBtn.addEventListener("click", () => {
    if (disposed || !active || voiceStarting) return;
    if (recorder) {
      recorder.stop();
      return;
    }
    void (async () => {
      const generation = ++voiceGeneration;
      voiceStarting = true;
      let microphone: MediaStream | null = null;
      try {
        const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
        microphone = stream;
        if (disposed || !active || generation !== voiceGeneration) {
          stream.getTracks().forEach((tr) => tr.stop());
          return;
        }
        const chunks: Blob[] = [];
        const preferred = ["audio/webm;codecs=opus", "audio/mp4", "audio/ogg;codecs=opus"]
          .find((mime) => MediaRecorder.isTypeSupported?.(mime));
        const rec = new MediaRecorder(stream, preferred ? { mimeType: preferred } : undefined);
        recorder = rec;
        rec.ondataavailable = (e) => {
          if (e.data.size) chunks.push(e.data);
        };
        rec.onstop = () => {
          stream.getTracks().forEach((tr) => tr.stop());
          recorder = null;
          voiceBtn.classList.remove("chat__tool--rec");
          voiceBtn.innerHTML = icon("voice");
          voiceBtn.setAttribute("aria-label", t("chat.voice"));
          voiceBtn.title = t("chat.voice");
          if (disposed || generation !== voiceGeneration) return;
          const mime = rec.mimeType || chunks.find((chunk) => chunk.type)?.type || "audio/webm";
          const blob = new Blob(chunks, { type: mime });
          const extension = mime.includes("mp4") ? "m4a" : mime.includes("ogg") ? "ogg" : "webm";
          void (async () => {
            // Пока STT идёт (upload+transcribe ~3-6с) — показываем статус.
            typing.textContent = t("chat.voice.processing");
            setTyping(true);
            try {
              // Голос идёт complete→transcript→текстом (voice_note в
              // chat/send запрещён — сервер вернёт 400).
              const res = await uploadFile(blob, "voice_note", `voice.${extension}`);
              if (disposed || generation !== voiceGeneration) return;
              const transcript = (res.transcript ?? "").trim();
              if (!transcript) {
                systemRow(t("chat.voice.empty"));
                return;
              }
              // Owner-decision 261001-vgd: транскрипт уходит Нике сразу —
              // без вставки в поле ввода (лишний мув). Пузырь покажет текст
              // целиком; черновик в поле не трогаем, чипы уходят с сообщением.
              await chipsLoading;
              enqueueSend(() => doSend(transcript));
            } catch {
              systemRow(t("chat.error.voice"));
            } finally {
              typing.textContent = t("chat.typing");
              if (!sending) setTyping(false);
            }
          })();
        };
        rec.start();
        voiceBtn.classList.add("chat__tool--rec");
        voiceBtn.innerHTML = icon("stop");
        voiceBtn.setAttribute("aria-label", t("chat.voice.stop"));
        voiceBtn.title = t("chat.voice.stop");
      } catch {
        microphone?.getTracks().forEach((tr) => tr.stop());
        systemRow(t("chat.error.voice"));
      } finally {
        voiceStarting = false;
      }
    })();
  });

  // Стартовая загрузка истории → живой стрим (курсор стрима — после истории).
  void (async () => {
    let lastId = 0;
    try {
      const { messages, welcome } = await getMessages(0);
      if (disposed) return;
      // Поздний ответ истории не должен вернуть привет поверх первого сообщения.
      if (welcome && messages.length === 0 && !empty.hidden) {
        const parts = [t("chat.welcome.body")];
        if (welcome.coach_name) {
          parts.unshift(t("chat.welcome.coach", { name: welcome.coach_name }));
        }
        if (welcome.trial_days !== null) {
          parts.push(plural("chat.welcome.trial", welcome.trial_days));
        }
        parts.push(t("chat.welcome.name"));
        empty.textContent = parts.join("\n\n");
        empty.classList.add("chat__empty--welcome");
      }
      for (const row of messages) {
        renderRow(row);
        lastId = Math.max(lastId, row.id);
      }
      scrollBottom();
    } catch {
      /* история догрузится стримом/polling */
    }
    // Восстановление после перезагрузки (DoD-4): активный черновик живёт в
    // D2-таблице — карточка возвращается без знания о прошлом ходе.
    if (disposed) return;
    try {
      const { operation } = await getActiveOperation();
      if (operation) renderOpCard(operation);
    } catch {
      /* карточка придёт событием стрима */
    }
    if (disposed) return;
    stopStream = startStream({
      after: lastId,
      onMessage: (row) => renderRow(row),
      onStatus: (status: StreamStatus) => {
        if (disposed) return;
        statusEl.textContent = t(`chat.status.${status}`);
        statusEl.parentElement!.hidden = status === "live";
      },
      onOperation: (card) => renderOpCard(card),
    });
  })();
  // ── suggest-чипсы (v2.1 UX-05): по времени суток, prefill без отправки.
  // При recovery_recent=true НЕ показываются вовсе (после срыва — только
  // человек-человек разговор, Core Value).
  const suggestEl = root.querySelector<HTMLElement>("#chat-suggest")!;

  const renderSuggestChips = (): void => {
    // v2.1 Phase 22 (UX-09): тренеру — один чипс «Итоги тренировки» (prefill).
    if (handlers.suggestCoach) {
      suggestEl.hidden = false;
      suggestEl.innerHTML = "";
      const b = document.createElement("button");
      b.type = "button";
      b.className = "suggest-chip";
      b.textContent = t("chips.coach_recap");
      b.addEventListener("click", () => {
        input.value = t("chips.coach_recap_text");
        input.focus();
      });
      suggestEl.appendChild(b);
      return;
    }
    if (!handlers.suggestChips) return;
    const hour = Number(
      new Intl.DateTimeFormat("en-GB", {
        timeZone: "Europe/Minsk",
        hour: "numeric",
        hour12: false,
      }).format(new Date()),
    );
    const labels: string[] =
      hour >= 6 && hour < 12
        ? [t("chips.morning1"), t("chips.morning2")]
        : hour >= 12 && hour < 17
          ? [t("chips.morning2")]
          : hour >= 17 && hour < 23
            ? [t("chips.evening")]
            : [];
    if (!labels.length) {
      suggestEl.hidden = true;
      suggestEl.innerHTML = "";
      return;
    }
    void fetchTodayCached()
      .then((td) => {
        if (td.recovery_recent) {
          suggestEl.hidden = true;
          suggestEl.innerHTML = "";
          return;
        }
        suggestEl.innerHTML = "";
        for (const label of labels) {
          const b = document.createElement("button");
          b.type = "button";
          b.className = "suggest-chip";
          b.textContent = label;
          b.addEventListener("click", () => {
            input.value = label;
            input.focus();
          });
          suggestEl.appendChild(b);
        }
        suggestEl.hidden = false;
      })
      .catch(() => {
        suggestEl.hidden = true;
      });
  };

  return {
    activate() {
      active = true;
      log.scrollTop = followBottom ? log.scrollHeight : savedScroll;
      renderSuggestChips();
    },
    deactivate() {
      if (active) savedScroll = log.scrollTop;
      active = false;
      discardVoice();
    },
    prefill(text: string) {
      input.value = text;
      input.focus();
    },
    dispose() {
      disposed = true;
      active = false;
      discardVoice();
      stopStream?.();
    },
  };
}
